import React, { useState } from 'react';
import FoldedComments, { defaultRenderMoreText } from './FoldedComments';
import HistoricalComment from './HistoricalComment';
import * as styles from './HistoricalCommentThread.module.scss';
import ReactionAvatarStack from './ReactionAvatarStack';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import Timestamp from '../../components/Timestamp';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import CommentContext from '../../utils/post/CommentContext';
import {
  isCommentOrPublicCommentThread,
  isReactionOrPublicReactionThread,
} from '../../utils/post/threadUtils';

const THREAD_FRAGMENT = gql(`
  fragment HistoricalCommentThread_thread on ThreadFields {
    id
    resolver {
      id
      name
    }
    ... on CommentThread {
      comments {
        id
        ...HistoricalComment_comment
      }
    }
    ... on ReactionThread {
      reactions {
        id
        ...ReactionAvatarStack_reaction
      }
    }
    resolvedAt
    context
  }
`);

interface HistoricalCommentThreadProps {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  onUnresolve: () => Promise<void>;
}

const HistoricalCommentThread = ({
  thread,
  onUnresolve,
}: HistoricalCommentThreadProps) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const [isResolving, setIsResolving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const resolved = thread.resolver && thread.resolvedAt;

  return (
    <>
      <div className={styles.resolverContainer}>
        <div className={styles.resolvedText}>
          {resolved ? (
            <>
              {currentUser.id === thread.resolver?.id
                ? 'You'
                : thread.resolver?.name}{' '}
              resolved <Timestamp date={thread.resolvedAt} />
            </>
          ) : (
            'Text deleted'
          )}
        </div>
        {resolved ? (
          <Button
            variant="link"
            size="small"
            disabled={isResolving}
            onClick={() => {
              setIsResolving(true);
              onUnresolve().catch(() => setIsResolving(false));
            }}
          >
            {isResolving ? <LoadingIndicator delay={0} /> : 'Unresolve'}
          </Button>
        ) : (
          <Tooltip
            trigger="clickOrHover"
            content={
              'Cannot unresolve comment\nbecause the commented text\nhas been deleted from the post'
            }
          >
            <div className={styles.outdatedText}>Outdated</div>
          </Tooltip>
        )}
      </div>
      <div id={`thread-${thread.id}`} className={styles.thread}>
        <div className={styles.context}>
          <CommentContext context={thread.context} />
        </div>
        {isReactionOrPublicReactionThread(thread) &&
          thread.reactions.length > 0 && (
            <div className={styles.reactionListContainer}>
              <ReactionAvatarStack reactions={thread.reactions} />
            </div>
          )}
        {isCommentOrPublicCommentThread(thread) && (
          <FoldedComments.Root
            key={thread.id}
            comments={thread.comments}
            expanded={isExpanded}
            renderComment={(comment) => (
              <HistoricalComment comment={comment} key={comment.id} />
            )}
            renderMoreText={(count) => (
              <Button variant="link" onClick={() => setIsExpanded(true)}>
                {defaultRenderMoreText(count)}
              </Button>
            )}
          />
        )}
      </div>
    </>
  );
};

export default HistoricalCommentThread;
